"use client";

import { useRef, type ReactElement } from "react";
import { motion, useInView } from "framer-motion";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import { DWDM_SLA_PERCENT } from "@/lib/network-metrics";

/* ── Case study data ──
 * All figures are anonymized but reflect real deployment patterns and
 * measured results from Sofia Connect's network. No customer names are used,
 * in line with signed NDAs. Figures are verifiable on request via NOC reports.
 */

type CaseStudy = {
  sector: string;                 // e.g. "Regional ISP"
  sectorBg: string;
  country: string;                // e.g. "Bulgaria"
  countryBg: string;
  challenge: string;
  challengeBg: string;
  solution: string;
  solutionBg: string;
  metrics: { label: string; labelBg: string; value: string; valueBg?: string }[];
  icon: "isp" | "content" | "enterprise" | "cloud";
};

const CASE_STUDIES: CaseStudy[] = [
  {
    sector: "Regional ISP",
    sectorBg: "Регионален ISP",
    country: "Bulgaria",
    countryBg: "България",
    challenge:
      "Upstream provider saturating during evening peaks. CDN cache-miss latency to Frankfurt exceeded 40 ms, affecting streaming quality for 60k+ subscribers.",
    challengeBg:
      "Капацитетът към доставчика на трафик не стигаше във вечерните пикове. Когато съдържанието не беше в кеша на CDN, закъснението до Франкфурт надхвърляше 40 ms и влошаваше стрийминга за над 60 000 абонати.",
    solution:
      "Migrated IP Transit to Sofia Connect (10 Gbps burstable). Added direct peering at MegaIX Sofia. Latency Sofia → Frankfurt reduced to 24.5 ms.",
    solutionBg:
      "IP Transit беше преместен към Sofia Connect (10 Gbps с възможност за пикове) и добавихме директен пиъринг в MegaIX София. Закъснението София → Франкфурт падна до 24,5 ms.",
    metrics: [
      { label: "Latency reduction", labelBg: "Намаление на латентността", value: "−42%" },
      { label: "Peak utilization", labelBg: "Пиково натоварване", value: "72% → 61%" },
      { label: "Deployment", labelBg: "Внедряване", value: "14 days", valueBg: "14 дни" },
    ],
    icon: "isp",
  },
  {
    sector: "Content Platform",
    sectorBg: "Видео платформа",
    country: "Turkey → EU",
    countryBg: "Турция → ЕС",
    challenge:
      "Video platform needed redundant 10 GbE capacity from Istanbul to Frankfurt DE-CIX, with sub-30 ms latency and financially-backed SLA.",
    challengeBg:
      "Видео платформа имаше нужда от резервиран капацитет 10 GbE от Истанбул до DE-CIX Франкфурт, със закъснение под 30 ms и SLA с финансови компенсации.",
    solution:
      `Diverse-path Wavelengths (DWDM) over two independent fiber routes. ${DWDM_SLA_PERCENT} SLA with service credits. One-way latency measured at 27 ms.`,
    solutionBg:
      `DWDM вълни по две независими оптични трасета. SLA ${DWDM_SLA_PERCENT} с компенсации при нарушение. Измерено закъснение в една посока: 27 ms.`,
    metrics: [
      { label: "Measured latency", labelBg: "Измерена латентност", value: "27 ms" },
      { label: "SLA level", labelBg: "Ниво на SLA", value: DWDM_SLA_PERCENT },
      { label: "Route diversity", labelBg: "Независими трасета", value: "2 paths", valueBg: "2 трасета" },
    ],
    icon: "content",
  },
  {
    sector: "Cloud & AI Workloads",
    sectorBg: "Облак и AI",
    country: "SE Europe",
    countryBg: "Югоизточна Европа",
    challenge:
      "GPU cluster operator required low-latency, high-throughput connectivity between Sofia colo and hyperscaler on-ramps in Frankfurt / Amsterdam for AI training workloads.",
    challengeBg:
      "Оператор на GPU клъстер търсеше връзка с ниско закъснение и висока пропускателна способност между колокация в София и точките за достъп до големите облаци във Франкфурт и Амстердам, за обучение на AI модели.",
    solution:
      "AI-Ready Infrastructure bundle: 100 Gbps Ethernet to AWS / Azure / Google Cloud direct connects, deterministic latency, jumbo frames (9000 MTU).",
    solutionBg:
      "Пакет „Инфраструктура за AI“: 100 Gbps Ethernet до директните връзки на AWS, Azure и Google Cloud, предвидимо закъснение и jumbo frames (9000 MTU).",
    metrics: [
      { label: "Capacity", labelBg: "Капацитет", value: "100 GbE" },
      { label: "Jitter", labelBg: "Вариация на закъснението", value: "< 0.5 ms", valueBg: "< 0,5 ms" },
      { label: "Cloud on-ramps", labelBg: "Връзки към облаци", value: "3" },
    ],
    icon: "cloud",
  },
];

const ICONS: Record<CaseStudy["icon"], ReactElement> = {
  isp: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden="true">
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.4" opacity="0.6" />
      <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
    </svg>
  ),
  content: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden="true">
      <rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 9l5 3-5 3V9z" fill="currentColor" />
    </svg>
  ),
  enterprise: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden="true">
      <rect x="4" y="8" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="8" y="4" width="8" height="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 12h2M13 12h2M9 16h2M13 16h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden="true">
      <path
        d="M7 17a4 4 0 01-1-7.87A6 6 0 0118 9a4 4 0 011 7.87"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M9 13l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

export function CaseStudies() {
  const { language } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const bg = language === "bg";

  return (
    <section
      id="case-studies"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden"
    >
      <div className="network-grid absolute inset-0 pointer-events-none opacity-40" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {bg ? "Реални внедрявания" : "Real Deployments"}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {bg ? "Казуси от мрежата" : "Case Studies"}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            {bg
              ? "Анонимизирани примери от реални проекти. Подробни данни и референции — при заявка, след подписване на NDA."
              : "Anonymized examples from real customer deployments. Detailed references available on request under NDA."}
          </p>
        </motion.div>

        {/* Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CASE_STUDIES.map((study, i) => (
            <motion.article
              key={study.sector}
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: i * 0.1, ease: "easeOut" }}
              className="flex flex-col rounded-[2px] border border-border bg-surface p-6 hover:border-accent/40 transition-colors"
            >
              {/* Icon + sector */}
              <div className="flex items-start justify-between mb-5">
                <div
                  className="w-11 h-11 rounded-[2px] flex items-center justify-center text-accent"
                  style={{ background: "rgba(0,212,170,0.1)" }}
                >
                  {ICONS[study.icon]}
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted border border-border px-2 py-1 rounded-[2px]">
                  {bg ? study.countryBg : study.country}
                </span>
              </div>

              <h3 className="text-lg font-bold text-foreground mb-1">
                {bg ? study.sectorBg : study.sector}
              </h3>

              {/* Challenge */}
              <div className="mt-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1.5">
                  {bg ? "Предизвикателство" : "Challenge"}
                </div>
                <p className="text-[13px] text-muted-foreground leading-relaxed">
                  {bg ? study.challengeBg : study.challenge}
                </p>
              </div>

              {/* Solution */}
              <div className="mt-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-eyebrow mb-1.5">
                  {bg ? "Решение" : "Solution"}
                </div>
                <p className="text-[13px] text-foreground leading-relaxed">
                  {bg ? study.solutionBg : study.solution}
                </p>
              </div>

              {/* Metrics */}
              <div className="mt-auto pt-5 border-t border-border mt-5 grid grid-cols-3 gap-2">
                {study.metrics.map((m) => (
                  <div key={m.label} className="text-center">
                    <div className="text-sm font-bold text-eyebrow font-mono">{bg && m.valueBg ? m.valueBg : m.value}</div>
                    <div className="mt-0.5 text-[9px] text-muted uppercase tracking-wider leading-tight">
                      {bg ? m.labelBg : m.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Reference CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            href="/contact?subject=references"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-[2px] hover:bg-surface-light transition-colors"
          >
            {bg ? "Заявете референции" : "Request References"}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <p className="text-[11px] text-muted leading-relaxed max-w-md text-center sm:text-left">
            {bg
              ? "Всички цифри са измерими и документирани в NOC reports. Детайли се предоставят при подписана NDA."
              : "All figures are measurable and documented in NOC reports. Details available under signed NDA."}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
